import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  FaArrowRight, 
  FaCode, 
  FaCloud, 
  FaRocket, 
  FaMobile,
  FaShieldAlt,
  FaChartLine,
  FaLinkedin,
  FaPlay
} from 'react-icons/fa';
import { 
  HiSparkles, 
  HiLightningBolt,
  HiCube,
  HiChip
} from 'react-icons/hi';
import './Home.css';

const Home = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const services = [
    {
      icon: <FaCode />,
      title: 'Custom Development',
      description: 'Bespoke software solutions engineered for your unique business challenges.',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud Architecture',
      description: 'Scalable cloud infrastructure designed for performance and reliability.',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)'
    },
    {
      icon: <FaMobile />,
      title: 'Mobile Solutions',
      description: 'Native and cross-platform apps that deliver exceptional experiences.',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cybersecurity',
      description: 'Enterprise-grade security solutions to protect your digital assets.',
      gradient: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)'
    },
    {
      icon: <FaChartLine />,
      title: 'Data Analytics',
      description: 'Transform raw data into actionable insights that drive growth.',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)'
    },
    {
      icon: <FaRocket />,
      title: 'Digital Transformation',
      description: 'End-to-end modernization strategies for the digital age.',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
    }
  ];

  const founders = [
    {
      name: 'Mayur Sagar',
      description: 'Strategic visionary passionate about leveraging technology to solve real-world challenges. Dedicated to building solutions that transform businesses and empower teams.',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      linkedin: 'https://www.linkedin.com/in/mayur-sagar-458a41283'
    },
    {
      name: 'Sahil Gite',
      description: 'Technology innovator with a deep commitment to crafting elegant, scalable solutions. Believes in the power of clean code and thoughtful architecture.',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
      linkedin: 'https://www.linkedin.com/in/sahilgite2003'
    },
    {
      name: 'Anushka Shinde',
      description: 'Operations strategist focused on delivering excellence through seamless execution. Champions quality, efficiency, and client satisfaction in every project.',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
      linkedin: 'https://www.linkedin.com/in/anushkashinde15'
    }
  ];

  const stats = [
    { value: '500+', label: 'Projects Delivered', icon: <HiCube /> },
    { value: '98%', label: 'Client Satisfaction', icon: <HiSparkles /> },
    { value: '50+', label: 'Tech Experts', icon: <HiChip /> },
    { value: '24/7', label: 'Support Available', icon: <HiLightningBolt /> }
  ];

  return (
    <div className="home-page">
      <Navbar />

      {/* === HERO SECTION === */}
      <section className="hero" ref={heroRef}>
        {/* Animated Background */}
        <div className="hero-bg">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
          <div className="hero-grid"></div>
          <div 
            className="hero-glow"
            style={{
              left: mousePosition.x,
              top: mousePosition.y,
            }}
          ></div>
        </div>

        {/* Floating Elements */}
        <div className="floating-elements">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
        </div>

        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <HiSparkles className="badge-icon" />
              <span>Next-Gen Technology Solutions</span>
            </div>

            <h1 className="hero-title">
              <span className="title-line">We Build</span>
              <span className="title-line gradient-text">Digital Excellence</span>
              <span className="title-line">For Tomorrow</span>
            </h1>

            <p className="hero-description">
              Cascade India transforms ambitious ideas into powerful digital realities. 
              We craft innovative software solutions that propel businesses into the future.
            </p>

            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Start Your Project
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                <FaPlay className="play-icon" />
                Explore Services
              </Link>
            </div>

            {/* Stats */}
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="stat-item"
                  style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                >
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* === SERVICES SECTION === */}
      <section className="section services-section">
        <div className="section-bg-pattern"></div>
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiLightningBolt />
              What We Do
            </div>
            <h2 className="section-title">
              Services That <span className="gradient-text">Elevate</span>
            </h2>
            <p className="section-description">
              Comprehensive technology solutions designed to transform your business 
              and keep you ahead of the competition.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="service-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div 
                  className="service-icon"
                  style={{ background: service.gradient }}
                >
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <div className="service-link">
                  <span>Learn More</span>
                  <FaArrowRight />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === FOUNDERS SECTION === */}
      <section className="section founders-section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiSparkles />
              Founders
            </div>
            <h2 className="section-title">
              Meet The <span className="gradient-text">Founders</span>
            </h2>
            <p className="section-description">
              The brilliant minds driving innovation and excellence at Cascade India.
            </p>
          </div>

          <div className="founders-grid">
            {founders.map((founder, index) => (
              <div 
                key={index} 
                className="founder-card"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="founder-card-inner">
                  <div 
                    className="founder-avatar"
                    style={{ background: founder.gradient }}
                  >
                    <span className="avatar-initials">
                      {founder.name.split(' ').map(n => n[0]).join('')}
                    </span>
                    <div className="avatar-glow" style={{ background: founder.gradient }}></div>
                  </div>
                  
                  <div className="founder-info">
                    <h3 className="founder-name">{founder.name}</h3>
                    <p className="founder-description">{founder.description}</p>
                    
                    <div className="founder-social">
                      <a href={founder.linkedin} className="social-link" target="_blank" rel="noopener noreferrer">
                        <FaLinkedin />
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="card-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === WHY CHOOSE US === */}
      <section className="section why-section">
        <div className="container">
          <div className="why-content">
            <div className="why-text">
              <div className="section-badge">
                <HiCube />
                Why Cascade India
              </div>
              <h2 className="section-title">
                Innovation Meets <span className="gradient-text">Excellence</span>
              </h2>
              <p className="why-description">
                We don't just build software – we architect digital experiences that 
                transform businesses. Our commitment to cutting-edge technology and 
                design-driven development sets us apart.
              </p>
              
              <div className="why-features">
                <div className="why-feature">
                  <div className="feature-check">✓</div>
                  <span>Enterprise-Grade Solutions</span>
                </div>
                <div className="why-feature">
                  <div className="feature-check">✓</div>
                  <span>Agile Development Process</span>
                </div>
                <div className="why-feature">
                  <div className="feature-check">✓</div>
                  <span>24/7 Dedicated Support</span>
                </div>
                <div className="why-feature">
                  <div className="feature-check">✓</div>
                  <span>Scalable Architecture</span>
                </div>
              </div>

              <Link to="/about" className="btn btn-primary">
                Discover More
                <FaArrowRight />
              </Link>
            </div>

            <div className="why-visual">
              <div className="visual-card card-1">
                <HiChip className="visual-icon" />
                <span>AI-Powered</span>
              </div>
              <div className="visual-card card-2">
                <FaCloud className="visual-icon" />
                <span>Cloud Native</span>
              </div>
              <div className="visual-card card-3">
                <FaShieldAlt className="visual-icon" />
                <span>Secure</span>
              </div>
              <div className="visual-orb"></div>
            </div>
          </div>
        </div>
      </section>

      {/* === CTA SECTION === */}
      <section className="section cta-section">
        <div className="cta-bg">
          <div className="cta-gradient"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to <span className="gradient-text">Transform</span> Your Business?
            </h2>
            <p className="cta-description">
              Let's build something extraordinary together. Get in touch and 
              let's discuss how we can bring your vision to life.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Get Started Today
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
